import { useState } from "react";
import { useRouter } from "next/navigation";
import Carousel from "@/components/views/list/card/Carousel";
import { useSite } from "@/lib/hooks/site";
import VenueCardNoImg from "@/components/views/list/card/VenueCardNoImg";
import S3Image from "@/components/S3Image";
import React from "react";
import { useHeaderTitle } from "@/hooks/useHeaderTitle";
import { useTranslation } from "@/hooks/translationContext";

interface Props extends Pick<Venue.List, "id" | "photos" | "descriptions"> {
  firstPriority?: boolean;
  setCurrentIndex: (index: number) => void;
  currentIndex: number;
  slug: string;
  districtSlug: string;
  /**
   * Overrides the per-site `venueCard.numberOfImages` setting. The homepage
   * passes 1 so its 24 cards ship one <img> each instead of four.
   */
  photoLimit?: number;
}

const photoSrc = (venueId: number, url: string) =>
  `/image/venue/${venueId}/photo/${url}?w=500&quality=60&height=356&format=webp&fit=contain`;

export default function VenueCardCarousel(props: Props) {
  const { t } = useTranslation();
  const [faultyImages, setFaultyImages] = useState<number[]>([]);
  const [highestMounted, setHighestMounted] = useState(1);

  const router = useRouter();
  const site = useSite();
  const { title: linkTitle, isVenueListPage } = useHeaderTitle();

  const PHOTO_LIMIT =
    props.photoLimit ?? site.setting.venueCard?.numberOfImages ?? 4;

  const handleError = (index: number) => {
    setFaultyImages((prevFaultyImages) => [...prevFaultyImages, index]);
  };

  const getLinkTitle = (index) =>
    t("listpage-card-photo-alt", {
      venue: `${props.descriptions?.[0]?.title}${isVenueListPage && linkTitle ? " - " + linkTitle : ""}`,
      index: index + 1,
    });

  const photos = Array.isArray(props.photos)
    ? props.photos.slice(0, Math.max(PHOTO_LIMIT, 1))
    : [];

  const openVenue = () =>
    router.push(
      `/${site.setting?.routes?.venueDetail}/${props.slug}-${props.districtSlug}`,
      { scroll: true },
    );

  const renderPhoto = (photo: { url: string }, index: number) => {
    if (faultyImages.includes(index)) return <VenueCardNoImg />;

    return (
      <S3Image
        onClick={openVenue}
        unoptimized={true}
        onError={() => handleError(index)}
        className="!w-full aspect-square rounded-[32px] bg-center object-cover object-center flex-shrink-0 bg-cover bg-no-repeat"
        src={photoSrc(props.id, photo.url)}
        priority={index === 0 && props.firstPriority}
        loading={index === 0 && props.firstPriority ? "eager" : "lazy"}
        width={356}
        height={356}
        alt={getLinkTitle(index)}
        title={getLinkTitle(index)}
        style={{
          maxWidth: "100%",
          backgroundColor: "#f5f5f5", // Light background while loading
        }}
      />
    );
  };

  const containerClassName =
    "relative group overflow-hidden cursor-grab active:cursor-grabbing bg-gray-50";
  const containerStyle = {
    aspectRatio: "1/1",
    touchAction: "pan-y",
    WebkitTouchCallout: "none",
    userSelect: "none",
  } as const;

  // With a single photo there is nothing to page through, so the carousel
  // chrome (arrows, dots, one absolutely positioned wrapper per slide) is
  // skipped entirely.
  if (photos.length <= 1) {
    return (
      <div className={containerClassName} style={containerStyle}>
        {photos.length === 1 ? (
          <div className="w-full h-full" style={{ aspectRatio: "1/1" }}>
            {renderPhoto(photos[0], 0)}
          </div>
        ) : null}
      </div>
    );
  }

  // Slides are mounted lazily: the visible one plus the next, so paging stays
  // instant while photos the visitor never reaches are never requested. The
  // high-water mark only grows, so images already shown are not torn down (and
  // re-fetched) when the user pages back.
  const mountedUpTo = Math.max(highestMounted, props.currentIndex + 1);
  if (mountedUpTo !== highestMounted) setHighestMounted(mountedUpTo);

  return (
    <div className={containerClassName} style={containerStyle}>
      <Carousel
        showButtons={true}
        hoverable={true}
        setItemIndex={props.setCurrentIndex}
        itemIndex={props.currentIndex}
        count={photos.length}
        dotColor="white"
      >
        {photos.map((photo, index) => (
          <div key={index} className="w-full h-full" style={{ aspectRatio: "1/1" }}>
            {index <= mountedUpTo ? renderPhoto(photo, index) : null}
          </div>
        ))}
      </Carousel>
    </div>
  );
}
